import type { TopzleGameDefinition } from "@topzle/game-sdk";
import type { BotContext, GameResult } from "@topzle/types";
import { z } from "zod";

export const CONNECT_FOUR_ROWS = 6;
export const CONNECT_FOUR_COLUMNS = 7;
const discSchema = z.enum(["red", "yellow"]);
const visualColorSchema = z.enum(["blue", "coral", "gold", "emerald", "cyan", "orange"]);
const cellSchema = z.union([discSchema, z.null()]);

export const connectFourStateSchema = z.object({
  board: z.array(cellSchema).length(CONNECT_FOUR_ROWS * CONNECT_FOUR_COLUMNS),
  players: z.object({ red: z.string(), yellow: z.string() }),
  colors: z
    .object({ red: visualColorSchema, yellow: visualColorSchema })
    .default({ red: "cyan", yellow: "gold" }),
  turn: discSchema,
  status: z.enum(["playing", "won", "draw"]),
  winner: discSchema.nullable(),
  winningCells: z.array(z.number().int().min(0).max(41)).length(4).nullable(),
  lastMove: z.number().int().min(0).max(41).nullable(),
  round: z.number().int().positive(),
  scores: z.record(z.string(), z.number().int().nonnegative()),
});

export const connectFourEventSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("drop"),
    column: z
      .number()
      .int()
      .min(0)
      .max(CONNECT_FOUR_COLUMNS - 1),
  }),
  z.object({ type: z.literal("rematch") }),
]);

export const connectFourSettingsSchema = z.object({
  firstDisc: discSchema.default("red"),
});

export type ConnectFourDisc = z.infer<typeof discSchema>;
export type ConnectFourVisualColor = z.infer<typeof visualColorSchema>;
export type ConnectFourState = z.infer<typeof connectFourStateSchema>;
export type ConnectFourEvent = z.infer<typeof connectFourEventSchema>;
export type ConnectFourSettings = z.infer<typeof connectFourSettingsSchema>;

const columnPreference = [3, 2, 4, 1, 5, 0, 6] as const;
const visualColors = visualColorSchema.options;
const winningWindows: number[][] = [];

for (let row = 0; row < CONNECT_FOUR_ROWS; row += 1) {
  for (let column = 0; column <= CONNECT_FOUR_COLUMNS - 4; column += 1) {
    winningWindows.push([0, 1, 2, 3].map((offset) => row * CONNECT_FOUR_COLUMNS + column + offset));
  }
}
for (let row = 0; row <= CONNECT_FOUR_ROWS - 4; row += 1) {
  for (let column = 0; column < CONNECT_FOUR_COLUMNS; column += 1) {
    winningWindows.push(
      [0, 1, 2, 3].map((offset) => (row + offset) * CONNECT_FOUR_COLUMNS + column),
    );
  }
}
for (let row = 0; row <= CONNECT_FOUR_ROWS - 4; row += 1) {
  for (let column = 0; column <= CONNECT_FOUR_COLUMNS - 4; column += 1) {
    winningWindows.push(
      [0, 1, 2, 3].map((offset) => (row + offset) * CONNECT_FOUR_COLUMNS + column + offset),
    );
    winningWindows.push(
      [0, 1, 2, 3].map((offset) => (row + 3 - offset) * CONNECT_FOUR_COLUMNS + column + offset),
    );
  }
}

function otherDisc(disc: ConnectFourDisc): ConnectFourDisc {
  return disc === "red" ? "yellow" : "red";
}

function normalizedRandomIndex(
  length: number,
  randomInt?: (maximumExclusive: number) => number,
): number {
  if (length <= 1) return 0;
  const value = randomInt?.(length) ?? 0;
  return Math.abs(Math.trunc(value)) % length;
}

export function randomConnectFourColors(
  randomInt?: (maximumExclusive: number) => number,
): ConnectFourState["colors"] {
  const red = visualColors[normalizedRandomIndex(visualColors.length, randomInt)] ?? "cyan";
  const remaining = visualColors.filter((color) => color !== red);
  const yellow = remaining[normalizedRandomIndex(remaining.length, randomInt)] ?? "gold";
  return { red, yellow };
}

export function connectFourDropIndex(
  board: ConnectFourState["board"],
  column: number,
): number | null {
  for (let row = CONNECT_FOUR_ROWS - 1; row >= 0; row -= 1) {
    const index = row * CONNECT_FOUR_COLUMNS + column;
    if (board[index] === null) return index;
  }
  return null;
}

export function connectFourWinningCells(board: ConnectFourState["board"]): number[] | null {
  for (const cells of winningWindows) {
    const disc = board[cells[0] ?? -1];
    if (disc && cells.every((index) => board[index] === disc)) return cells;
  }
  return null;
}

function availableColumns(board: ConnectFourState["board"]): number[] {
  return columnPreference.filter((column) => connectFourDropIndex(board, column) !== null);
}

function boardAfterDrop(
  board: ConnectFourState["board"],
  column: number,
  disc: ConnectFourDisc,
): ConnectFourState["board"] | null {
  const index = connectFourDropIndex(board, column);
  if (index === null) return null;
  const next = [...board];
  next[index] = disc;
  return next;
}

function winningColumns(board: ConnectFourState["board"], disc: ConnectFourDisc): number[] {
  return availableColumns(board).filter((column) => {
    const next = boardAfterDrop(board, column, disc);
    return next ? connectFourWinningCells(next) !== null : false;
  });
}

function isPlayableCell(board: ConnectFourState["board"], index: number): boolean {
  return connectFourDropIndex(board, index % CONNECT_FOUR_COLUMNS) === index;
}

function scorePosition(board: ConnectFourState["board"], bot: ConnectFourDisc): number {
  const opponent = otherDisc(bot);
  let score = 0;
  let botThreats = 0;
  let opponentThreats = 0;
  const columnWeights = [3, 5, 7, 10, 7, 5, 3] as const;
  for (let row = 0; row < CONNECT_FOUR_ROWS; row += 1) {
    for (let column = 0; column < CONNECT_FOUR_COLUMNS; column += 1) {
      const disc = board[row * CONNECT_FOUR_COLUMNS + column];
      if (disc === bot) score += columnWeights[column] ?? 0;
      if (disc === opponent) score -= columnWeights[column] ?? 0;
    }
  }
  for (const cells of winningWindows) {
    const botCount = cells.filter((index) => board[index] === bot).length;
    const opponentCount = cells.filter((index) => board[index] === opponent).length;
    const emptyCount = 4 - botCount - opponentCount;
    const emptyIndex = emptyCount === 1 ? cells.find((index) => board[index] === null) : undefined;
    const playableEmpty = emptyIndex !== undefined && isPlayableCell(board, emptyIndex);
    if (opponentCount === 0) {
      if (botCount === 3 && emptyCount === 1) {
        score += playableEmpty ? 520 : 95;
        if (playableEmpty) botThreats += 1;
      } else if (botCount === 2 && emptyCount === 2) score += 34;
      else if (botCount === 1) score += 4;
    }
    if (botCount === 0) {
      if (opponentCount === 3 && emptyCount === 1) {
        score -= playableEmpty ? 650 : 115;
        if (playableEmpty) opponentThreats += 1;
      } else if (opponentCount === 2 && emptyCount === 2) score -= 42;
      else if (opponentCount === 1) score -= 4;
    }
  }
  score += botThreats * 760;
  score -= opponentThreats * 920;
  if (botThreats >= 2) score += 18_000;
  if (opponentThreats >= 2) score -= 22_000;
  return score;
}

function opponentForkReplies(board: ConnectFourState["board"], opponent: ConnectFourDisc): number {
  return availableColumns(board).filter((column) => {
    const reply = boardAfterDrop(board, column, opponent);
    return reply ? winningColumns(reply, opponent).length >= 2 : false;
  }).length;
}

function minimax(
  board: ConnectFourState["board"],
  depth: number,
  turn: ConnectFourDisc,
  bot: ConnectFourDisc,
  alpha: number,
  beta: number,
  memo: Map<string, number>,
): number {
  const winning = connectFourWinningCells(board);
  if (winning) return board[winning[0] ?? -1] === bot ? 1_000_000 + depth : -1_000_000 - depth;
  const columns = availableColumns(board);
  if (columns.length === 0) return 0;
  if (depth === 0) return scorePosition(board, bot);
  const key = `${board.map((cell) => cell?.[0] ?? ".").join("")}:${depth}:${turn}`;
  const cached = memo.get(key);
  if (cached !== undefined) return cached;

  let best = turn === bot ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY;
  let cutoff = false;
  for (const column of columns) {
    const next = boardAfterDrop(board, column, turn);
    if (!next) continue;
    const score = minimax(next, depth - 1, otherDisc(turn), bot, alpha, beta, memo);
    if (turn === bot) {
      best = Math.max(best, score);
      alpha = Math.max(alpha, best);
    } else {
      best = Math.min(best, score);
      beta = Math.min(beta, best);
    }
    if (alpha >= beta) {
      cutoff = true;
      break;
    }
  }
  if (!cutoff) memo.set(key, best);
  return best;
}

export function chooseConnectFourBotColumn(
  state: ConnectFourState,
  difficulty: BotContext["difficulty"],
): number {
  const available = availableColumns(state.board);
  if (available.length === 0) throw new Error("No legal columns");
  const bot = state.turn;
  const opponent = otherDisc(bot);
  const winningMove = winningColumns(state.board, bot)[0];
  if (winningMove !== undefined) return winningMove;

  const blockingMove = winningColumns(state.board, opponent)[0];
  if (blockingMove !== undefined) return blockingMove;
  if (difficulty === "easy") {
    const moveCount = state.board.filter(Boolean).length;
    return available[(moveCount * 3 + 1) % available.length] ?? available[0] ?? 3;
  }

  const safeColumns = available.filter((column) => {
    const board = boardAfterDrop(state.board, column, bot);
    return board ? winningColumns(board, opponent).length === 0 : false;
  });
  const candidates = safeColumns.length > 0 ? safeColumns : available;
  if (difficulty === "hard") {
    const forkMove = candidates.find((column) => {
      const board = boardAfterDrop(state.board, column, bot);
      return board ? winningColumns(board, bot).length >= 2 : false;
    });
    if (forkMove !== undefined) return forkMove;
  }

  const occupied = state.board.filter(Boolean).length;
  const remaining = state.board.length - occupied;
  const depth =
    difficulty === "medium"
      ? occupied < 10
        ? 4
        : 5
      : remaining <= 10
        ? remaining
        : remaining <= 18
          ? 7
          : 6;
  const memo = new Map<string, number>();
  const scored = candidates.map((column) => {
    const board = boardAfterDrop(state.board, column, bot);
    const botForks = board ? winningColumns(board, bot).length : 0;
    const dangerousForkReplies = board ? opponentForkReplies(board, opponent) : 0;
    return {
      column,
      score: board
        ? minimax(
            board,
            Math.max(0, depth - 1),
            opponent,
            bot,
            Number.NEGATIVE_INFINITY,
            Number.POSITIVE_INFINITY,
            memo,
          ) +
          botForks * 12_000 -
          dangerousForkReplies * 15_000
        : Number.NEGATIVE_INFINITY,
    };
  });
  scored.sort(
    (left, right) =>
      right.score - left.score ||
      columnPreference.indexOf(left.column as (typeof columnPreference)[number]) -
        columnPreference.indexOf(right.column as (typeof columnPreference)[number]),
  );
  return scored[0]?.column ?? available[0] ?? 3;
}

export const connectFourGame: TopzleGameDefinition<
  ConnectFourState,
  ConnectFourEvent,
  ConnectFourSettings
> = {
  id: "connect-four",
  version: "1.0.0",
  name: "Connect 4",
  shortDescription: "Drop discs, build threats, and connect four before your rival.",
  longDescription:
    "The classic vertical strategy game with authoritative gravity, smart local bots, spectators, and instant rematches.",
  category: "board",
  icon: "circle-dot",
  cover: "connect-four",
  minPlayers: 2,
  maxPlayers: 2,
  supportsGuests: true,
  supportsLocalPlay: true,
  supportsOnlinePlay: true,
  supportsSpectators: true,
  supportsTeams: false,
  supportsAiPlayers: true,
  supportsLocalAi: true,
  supportsCloudAi: false,
  supportsTournaments: true,
  supportsPersistence: true,
  requiredCapabilities: ["text"],
  supportedLayouts: ["bubbles", "rail", "game_fullscreen", "split", "avatar_only"],
  preferredLayout: "game_fullscreen",
  stateSchema: connectFourStateSchema,
  eventSchema: connectFourEventSchema,
  settingsSchema: connectFourSettingsSchema,
  createInitialState(context, settings) {
    const [red = "player-red", yellow = "player-yellow"] = context.playerIds;
    return {
      board: Array(CONNECT_FOUR_ROWS * CONNECT_FOUR_COLUMNS).fill(null),
      players: { red, yellow },
      colors: randomConnectFourColors(context.randomInt),
      turn: settings.firstDisc,
      status: "playing",
      winner: null,
      winningCells: null,
      lastMove: null,
      round: 1,
      scores: { [red]: 0, [yellow]: 0 },
    };
  },
  validateEvent(state, event, context) {
    if (event.type === "rematch")
      return {
        valid: state.status !== "playing",
        code: "round_active",
        message: "Finish this round first",
      };
    if (state.status !== "playing")
      return { valid: false, code: "round_finished", message: "The round is over" };
    if (state.players[state.turn] !== context.actorId)
      return { valid: false, code: "not_your_turn", message: "Wait for your turn" };
    if (connectFourDropIndex(state.board, event.column) === null)
      return { valid: false, code: "column_full", message: "That column is full" };
    return { valid: true };
  },
  reduceEvent(state, event, context) {
    if (event.type === "rematch")
      return {
        ...state,
        board: Array(CONNECT_FOUR_ROWS * CONNECT_FOUR_COLUMNS).fill(null),
        colors: randomConnectFourColors(context.randomInt),
        turn: state.round % 2 === 1 ? "yellow" : "red",
        status: "playing",
        winner: null,
        winningCells: null,
        lastMove: null,
        round: state.round + 1,
      };
    const lastMove = connectFourDropIndex(state.board, event.column);
    if (lastMove === null) return state;
    const board = [...state.board];
    board[lastMove] = state.turn;
    const winningCells = connectFourWinningCells(board);
    const boardFull = board.every(Boolean);
    const winner = winningCells ? state.turn : null;
    const scores = { ...state.scores };
    if (winner) {
      const winnerId = state.players[winner];
      scores[winnerId] = (scores[winnerId] ?? 0) + 1;
    }
    return {
      ...state,
      board,
      turn: otherDisc(state.turn),
      status: winner ? "won" : boardFull ? "draw" : "playing",
      winner,
      winningCells,
      lastMove,
      scores,
    };
  },
  getPublicState: (state) => state,
  getPrivatePlayerState: () => ({}),
  getSpectatorState: (state) => state,
  getAvailableActions(state, playerId) {
    if (state.status !== "playing" || state.players[state.turn] !== playerId) return [];
    return availableColumns(state.board).map((column) => ({ type: "drop", column }));
  },
  calculateResult(state): GameResult | null {
    if (state.status === "playing") return null;
    const winnerId = state.winner ? state.players[state.winner] : null;
    return {
      status: winnerId ? "completed" : "draw",
      winnerIds: winnerId ? [winnerId] : [],
      scores: state.scores,
      reason: winnerId ? "four_connected" : "board_full",
    };
  },
  async createBotAction(state, botContext) {
    if (botContext.policy === "random") {
      const available = availableColumns(state.board);
      const column =
        available[
          Math.abs(botContext.now + state.board.filter(Boolean).length * 13) % available.length
        ];
      if (column === undefined) throw new Error("No legal columns");
      return { type: "drop", column };
    }
    const difficulty = ["easy", "medium", "hard"].includes(botContext.policy ?? "")
      ? (botContext.policy as BotContext["difficulty"])
      : botContext.difficulty;
    return { type: "drop", column: chooseConnectFourBotColumn(state, difficulty) };
  },
  handlePlayerDeparture(state, departure) {
    const disc =
      state.players.red === departure.playerId
        ? "red"
        : state.players.yellow === departure.playerId
          ? "yellow"
          : null;
    if (!disc || !departure.replacementPlayerId) return null;
    const replacementPlayerId = departure.replacementPlayerId;
    const scores = { ...state.scores };
    scores[replacementPlayerId] = scores[departure.playerId] ?? 0;
    delete scores[departure.playerId];
    return {
      state: {
        ...state,
        players: { ...state.players, [disc]: replacementPlayerId },
        scores,
      },
      replacementPlayerId,
    };
  },
};
